import Image from 'next/image';
import React, { useEffect } from 'react';
import Pill from '@/components/UI/Pill';
import ScrollMotion100Once from '@/framer/animateWhileInView/ScrollMotion100Once';
import useWindowSize from '@/hooks/useWindowSize';
import type { ReactElement } from 'react';

export enum PillLocation {
  Border = '-translate-y-[15px]',
  Inside = 'p-8',
}

interface DarkCardProps {
  reverseOrder?: boolean;
  imgDesktop: ReactElement;
  imgMobile: ReactElement;
  pillTitle: string;
  titleOne: string;
  titleTwo: string;
  content: string;
  features: string[];
}

const CheckInfo = () => (
  <Image
    src={'/img/CheckInfoCard.svg'}
    width={20}
    height={20}
    alt="CheckInfo"
  />
);

const DarkCard = ({
  reverseOrder = true,
  imgDesktop,
  imgMobile,
  pillTitle,
  titleTwo,
  titleOne,
  content,
  features,
}: DarkCardProps) => {
  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = React.useState(false);

  useEffect(() => {
    setIsMobile(width <= 768);
  }, [width]);

  const finalReverseOrder = !isMobile && reverseOrder;

  return (
    <div
      className={`relative mx-4 h-full max-w-[1120px] rounded-[16px] bg-primary-hover ${
        isMobile ? 'flex flex-col' : 'grid md:grid-cols-2'
      } ${
        finalReverseOrder
          ? 'md:grid-cols-2-reverse md:grid-rows-[auto,1fr]'
          : 'md:grid-rows-[1fr,auto]'
      }`}
    >
      <div
        className={`relative flex flex-grow flex-col items-start justify-between pl-9 pt-9 md:items-center md:pl-0 md:pt-9 ${
          finalReverseOrder ? 'order-2 md:order-1' : 'order-1 md:order-2'
        }`}
      >
        <div className="flex flex-col items-start px-4 md:items-center">
          <ScrollMotion100Once>
            <Pill>{pillTitle}</Pill>
          </ScrollMotion100Once>
          <ScrollMotion100Once>
            <h1 className="block pt-6 text-left font-gt-haptik text-heading-3-mobile font-bold text-white md:text-center md:text-heading-3">
              {titleOne}
            </h1>
          </ScrollMotion100Once>
          <ScrollMotion100Once>
            <span className="-mt-4 block bg-accent-main bg-clip-text text-left font-gt-haptik text-heading-3-mobile text-transparent md:text-center md:text-heading-3">
              {titleTwo}
            </span>
          </ScrollMotion100Once>
        </div>
        {!isMobile && (
          <div
            className={
              'flex max-h-[300px] flex-shrink flex-grow justify-end overflow-clip rounded-lg md:flex'
            }
          >
            {imgDesktop}
          </div>
        )}
      </div>

      <div
        className={`my-auto px-[52px] py-4 md:py-12  ${
          finalReverseOrder ? 'order-1 md:order-2' : 'order-2 md:order-1'
        }`}
      >
        <p>{content}</p>
        {features.map((feature, index) => (
          <div key={index} className="flex-2 flex w-full pt-4">
            <div className="flex-shrink-0">
              <CheckInfo />
            </div>
            <p className="pl-2">{feature}</p>
          </div>
        ))}
      </div>

      {isMobile && (
        <div
          className={`flex flex-shrink-0 items-center justify-center ${
            finalReverseOrder ? 'order-1 md:order-2' : 'order-2 md:order-1'
          }`}
        >
          {imgMobile}
        </div>
      )}
    </div>
  );
};

export default DarkCard;
